import { environment } from '../../../environments/environment';

/**
 * Safari (y todos los navegadores en iOS, por WebKit/ITP) puede bloquear cookies
 * de terceros aunque se envien con credentials, afectando sesiones HTTP-only.
 * En esos casos, el fallback a JWT/sessionId solo se activa si el environment lo permite.
 */
function shouldForceTokenMode(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent;

  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
  const isSafariDesktop = /Safari/i.test(userAgent) && !/Chrome|Chromium|CriOS|Edg|OPR|Firefox|FxiOS/i.test(userAgent);

  return isIOS || isSafariDesktop;
}

const forceTokenMode = environment.api.cookie_http_only && shouldForceTokenMode();
const allowSafariTokenFallback = environment.api.allowSafariTokenFallback ?? false;

export const API_CONFIG = {
  logEnabled: environment.api.logEnabled,
  baseUrl: environment.api.baseUrl,
  api_key: environment.api.api_key,
  logoUrl: environment.api.logoUrl,
  seo: environment.api.seo,
  footerInfo: environment.api.footerInfo,
  cookie_http_only: environment.api.cookie_http_only && !(allowSafariTokenFallback && forceTokenMode),
  force_token_mode: allowSafariTokenFallback && forceTokenMode,
  allow_safari_token_fallback: allowSafariTokenFallback,
  endpoints: {
    webapp:{
      configurations: '/configurations/webapp'
    },
    auth: {
      login: '/auth/login',
      logout: '/auth/logout',
      refresh: '/auth/refresh',
      logoutAll: '/auth/logout-all',
      profle: '/auth/me',//get
      checkEmail: '/auth/check-email', // POST - Verificar disponibilidad de email
      register: '/auth/register', // POST - Registro de usuario con auto-login
      activate: '/auth/activate', // GET/POST - Activación de cuenta
      resendActivation: '/auth/resend-activation', // POST - Reenviar activación
      requestPasswordReset: '/auth/request-password-reset', // POST - Solicitar recuperación
      confirmPasswordReset: '/auth/confirm-password-reset', // POST - Confirmar recuperación
      changePassword: '/auth/change-password', // POST - Cambiar contraseña (sesión activa)
    },
    guestSessions: {
      // Endpoints estándar (modo manual - session ID en localStorage)
      create: '/guest-sessions',
      verify: '/guest-sessions/{session_id}/verify',
      get: '/guest-sessions/{session_id}',
      delete: '/guest-sessions/{session_id}',
      // Endpoints con cookies HTTP-only (modo cookie - session ID en cookie)
      createCookie: '/sessions',
      verifyCookie: '/sessions/verify'
    },
    users: {
      create: '/users',
      list: '/users',
      getById: '/users/{id}',
      searchByEmail: '/users/search/by-email',
      updateProfile: '/users/{id}/profile',
      updateRole: '/users/{id}/role',
      updateFacturacion: '/users/{id}/facturacion',
      deleteFacturacion: '/users/{id}/facturacion',
      delete: '/users/{id}',
      resetPassword: '/users/{id}/reset-password',
      changePassword: '/users/{id}/change-password'
    },
    organizadores: {
      create: '/organizadores',
      list: '/organizadores',
      getById: '/organizadores/{id}',
      getByUser: '/organizadores/user/{userid}',
      update: '/organizadores/{id}',
      updateFacturacion: '/organizadores/{id}/facturacion',
      updateEstado: '/organizadores/{id}/estado',
      delete: '/organizadores/{id}'
    },
    eventos: {
      create: '/eventos',
      list: '/eventos',
      listPublic: '/events/public',
      getById: '/eventos/{id}',
      getFull: '/eventos/{id}/full',
      getBySlug: '/events/{url_slug}',
      getByOrganizador: '/eventos/organizador/{organizadorid}',
      update: '/eventos/{id}',
      updateEstado: '/eventos/{id}/estado',
      updateVisibilidad: '/eventos/{id}/visibilidad',
      publish: '/eventos/{id}/publish',
      delete: '/eventos/{id}',
      getLegalDocs: '/eventos/{eventoid}/legal-docs',
      getMetodosPago: '/eventos/{eventoid}/metodos-pago',
      getMetodoPago: '/eventos/{eventoid}/metodos-pago/{metodopagoid}',
      getEventoForms: '/eventos/{eventoid}/forms',
      // Subeventos (Actividades)
      getSubeventos: '/eventos/{eventoid}/subeventos',
      getSubeventoBySlug: '/eventos/{eventoid}/subeventos/by-slug/{slug}',
      getSubeventoFechas: '/eventos/{eventoid}/subeventos/{subeventoid}/fechas',
      getSubeventoTipoTicketsByFecha: '/eventos/{eventoid}/subeventos/{subeventoid}/fechas/{fecha}/tipotickets'
    },
    tiposTicket: {
      create: '/tipos-ticket',
      list: '/tipos-ticket',
      getById: '/tipos-ticket/{id}',
      getByIdForWeb: '/tipos-ticket/{tipoticketid}',
      getByEvento: '/tipos-ticket/evento/{eventoid}',
      getTicketsByEstado: '/tipos-ticket/{tipoticketid}/tickets-by-estado',
      update: '/tipos-ticket/{id}',
      updateEstado: '/tipos-ticket/{id}/estado',
      delete: '/tipos-ticket/{id}',
      // Reserva y liberación
      reserve: '/tipos-ticket/{id}/reserve',
      release: '/tipos-ticket/{id}/release',
      // Métodos de pago
      addMetodoPago: '/tipos-ticket/{id}/metodos-pago',
      listMetodosPago: '/tipos-ticket/{id}/metodos-pago',
      getMetodoPago: '/tipos-ticket/{id}/metodos-pago/{metodoid}',
      updateMetodoPago: '/tipos-ticket/{id}/metodos-pago/{metodoid}',
      deleteMetodoPago: '/tipos-ticket/{id}/metodos-pago/{metodoid}',
      // Precios
      addPrecio: '/tipos-ticket/{id}/precios',
      listPrecios: '/tipos-ticket/{id}/precios',
      getPrecio: '/tipos-ticket/{id}/precios/{precioid}',
      updatePrecio: '/tipos-ticket/{id}/precios/{precioid}',
      deletePrecio: '/tipos-ticket/{id}/precios/{precioid}'
    },
    tickets: {
      // create: '/tickets', // Removed - tickets are created via cart
      // list: '/tickets', // Removed
      getById: '/tickets/{id}',
      // getByUser: '/tickets/user/{userid}', // Removed
      // getByEvento: '/tickets/evento/{eventoid}', // Removed
      updateEstado: '/tickets/{id}/estado'
      // cancel: '/tickets/{id}/cancel' // Removed
    },
    carrito: {
      // Agregar tickets
      addByQuantity: '/cart/add-tickets-by-quantity',
      addTicket: '/cart/add-ticket',
      changeTicketPrecio: '/cart/change-ticket-precio',
      // Obtener carrito
      get: '/cart',
      getById: '/cart/{cartid}',
      getBySession: '/cart/session',
      getByUser: '/cart/user',
      // Remover items
      removeTicket: '/cart/{cartid}/remove-ticket/{ticketid}',
      removeGroup: '/cart/{cartid}/remove-group/{grupoid}',
      // Limpiar y completar
      clear: '/cart/{cartid}',
      complete: '/cart/{cartid}/complete',
      // Generar payment ID
      generatePaymentId: '/cart/generate-payment-id',
      // Registrar fallo de pago
      paymentFailure: '/cart/payment-failure',
      // Preparar carrito para pasarela (renueva si el cart ya fue usado)
      preparePayphone: '/cart/{cartid}/payphone/prepare',
      // Extender tiempo del carrito mientras el usuario está en la caja de pago
      extendTime: '/cart/{cartid}/extend-time',
      // Extender tiempo con precisión de minutos + segundos
      extendTimePrecise: '/cart/{cartid}/extend-time-precise',
      // Obtener por payment ID
      getByPaymentId: '/cart/by-payment-id/{paymentid}'
    },
    configurations: {
      list: '/configurations',
      getById: '/configurations/{sk}',
      // Email
      createEmail: '/configurations/email',
      getEmail: '/configurations/email',
      // Company
      createCompany: '/configurations/company',
      getCompany: '/configurations/company',
      // Privacy Policy
      createPrivacyPolicy: '/configurations/privacy-policy',
      getPrivacyPolicy: '/configurations/privacy-policy',
      // Google Maps
      getGoogleMaps: '/configurations/google-maps'
    },
    tiposIdentificacion: {
      list: '/tipos-identificacion'
    },
    facturas: {
      create: '/facturas',
      myInvoices: '/facturas/my-invoices',
      getDetails: '/facturas/{facturaid}/details',
      resendEmail: '/facturas/{facturaid}/resend-email'
    }
  },
  storage: {
    tokenKey: 'access_token',
    refreshTokenKey: 'refresh_token',
    userKey: 'current_user',
    guestSessionKey: 'guest_session_id'
  },
  guestSession: {
    defaultExpirationMinutes: 30,
    extensionMinutes: 30,
    verifyIntervalMinutes: 15
  },
  labels: {
    servicioEmisionTickets: 'Servicio Emisión de Tickets'
  }
} as const;
